import { createRole } from '../../../access-approvals/roles/services/role.service';

/**
 * Every department has one Blocks IAM role, "Head of Department", whose key IS the department's Key
 * (`hrm::department-scope::<organization-slug>::<department-slug>`). Holding it in an organization
 * makes a person the head of that department there: the data rules (HOB Rule) and the workflows
 * match it against a record's DepartmentKey. It grants no permission by itself; the head_of_business
 * role (a permission group) is what opens the department screens. People get it from the
 * directory's role dialog.
 *
 * The role belongs to the department, not to the Role Manager (which never lists it). Deleting a
 * department (a soft delete) leaves its role: IAM can't restore a deleted role, and the department's
 * records stay reachable by its heads.
 */

export const departmentRoleName = (title: string, organizationName: string) =>
  `Head of Department: ${organizationName} / ${title}`;

/** Creates the department's Head of Department role, unless it exists. Throws `ROLE_KEY_ARCHIVED`
 * when a role with this key was deleted. */
export const createDepartmentRole = ({
  key,
  title,
  organizationName,
}: {
  key: string;
  title: string;
  organizationName: string;
}) =>
  createRole({
    slug: key,
    name: departmentRoleName(title, organizationName),
    description: `Head of the "${title}" department of ${organizationName}: sees its records`,
  });

/** Gives every department its role — for departments whose role IAM refused at create time. One
 * department at a time, so a failure is counted per department instead of stopping the rest. */
export const syncDepartmentRoles = async (
  departments: { Title: string; Key: string }[],
  organizationName: string
) => {
  const summary = { existing: 0, created: 0, failed: 0 };
  for (const department of departments) {
    try {
      const result = await createDepartmentRole({
        key: department.Key,
        title: department.Title,
        organizationName,
      });
      summary[result]++;
    } catch (error) {
      console.error(`Failed to sync the role of ${department.Key}`, error);
      summary.failed++;
    }
  }
  return summary;
};
